/**
 * AudioSystem - Synthesized audio for helicopter simulation
 * Implements rotor sounds, weapon sounds, and voice warnings
 */

export class AudioSystem {
  private audioContext: AudioContext | null = null;
  private rotorNoiseNode: ScriptProcessorNode | null = null;
  private rotorGain: GainNode | null = null;
  private rotorFilter: BiquadFilterNode | null = null;
  private rotorLFO: OscillatorNode | null = null;
  private rotorLFOGain: GainNode | null = null;
  private masterGain: GainNode | null = null;
  private isRotorPlaying: boolean = false;
  private utterances: Map<string, SpeechSynthesisUtterance>;

  constructor() {
    this.utterances = new Map();
    this.initializeWarnings();
  }

  /**
   * Initialize audio context (must be called after user interaction)
   */
  initialize(): void {
    if (this.audioContext) return;

    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Resume if suspended (Chrome autoplay policy)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);
      this.masterGain.gain.value = 0.3; // Master volume

      this.setupRotorSound();
    } catch (error) {
      console.warn('Audio initialization failed:', error);
    }
  }

  /**
   * Set up continuous rotor sound
   */
  private setupRotorSound(): void {
    if (!this.audioContext) return;

    // Create brown noise using white noise filtered
    const bufferSize = 4096;
    this.rotorNoiseNode = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
    let lastOut = 0.0;
    
    this.rotorNoiseNode.onaudioprocess = (e) => {
      const output = e.outputBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        output[i] = (lastOut + (0.02 * white)) / 1.02;
        lastOut = output[i];
        output[i] *= 3.5; // Amplify
      }
    };

    // Low-pass filter for "thump" character
    this.rotorFilter = this.audioContext.createBiquadFilter();
    this.rotorFilter.type = 'lowpass';
    this.rotorFilter.frequency.value = 200;
    this.rotorFilter.Q.value = 1;

    // LFO (Low Frequency Oscillator) for "chop-chop" rhythm
    this.rotorLFO = this.audioContext.createOscillator();
    this.rotorLFO.frequency.value = 13; // ~13 Hz for rotor blade rate
    this.rotorLFO.type = 'sine';

    this.rotorLFOGain = this.audioContext.createGain();
    this.rotorLFOGain.gain.value = 30; // Modulation depth

    // Connect LFO to filter frequency
    this.rotorLFO.connect(this.rotorLFOGain);
    this.rotorLFOGain.connect(this.rotorFilter.frequency);

    // Main gain for rotor
    this.rotorGain = this.audioContext.createGain();
    this.rotorGain.gain.value = 0;

    // Connect: brownNoise -> filter -> gain -> master
    this.rotorNoiseNode.connect(this.rotorFilter);
    this.rotorFilter.connect(this.rotorGain);
    this.rotorGain.connect(this.masterGain!);

    this.rotorLFO.start();
  }

  /**
   * Start rotor sound
   */
  startRotor(): void {
    if (!this.audioContext || this.isRotorPlaying) return;

    this.initialize(); // Ensure initialized
    
    if (this.rotorGain) {
      // Fade in rotor sound
      this.rotorGain.gain.setValueAtTime(0, this.audioContext.currentTime);
      this.rotorGain.gain.linearRampToValueAtTime(0.4, this.audioContext.currentTime + 2);
      this.isRotorPlaying = true;
    }
  }

  /**
   * Stop rotor sound
   */
  stopRotor(): void {
    if (!this.audioContext || !this.isRotorPlaying) return;

    if (this.rotorGain) {
      // Fade out rotor sound
      this.rotorGain.gain.setValueAtTime(this.rotorGain.gain.value, this.audioContext.currentTime);
      this.rotorGain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 1);
      this.isRotorPlaying = false;
    }
  }

  /**
   * Update rotor sound based on collective
   */
  updateRotorPitch(collective: number): void {
    if (!this.audioContext || !this.rotorLFO) return;

    // Adjust frequency based on collective (simulates rotor RPM)
    const baseFreq = 13;
    const targetFreq = baseFreq + (collective * 3);
    this.rotorLFO.frequency.setValueAtTime(targetFreq, this.audioContext.currentTime);

    // Adjust filter cutoff for load
    if (this.rotorFilter) {
      const baseCutoff = 200;
      const targetCutoff = baseCutoff + (collective * 50); // Reduced modulation to avoid conflict with LFO
      this.rotorFilter.frequency.setValueAtTime(targetCutoff, this.audioContext.currentTime);
    }
  }

  /**
   * Play chain gun sound
   */
  playChainGun(): void {
    if (!this.audioContext) return;

    // Short burst of noise
    const noise = this.audioContext.createBufferSource();
    const buffer = this.audioContext.createBuffer(1, this.audioContext.sampleRate * 0.1, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (this.audioContext.sampleRate * 0.02));
    }
    
    noise.buffer = buffer;

    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 300;

    const gain = this.audioContext.createGain();
    gain.gain.value = 0.15;

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    noise.start();
  }

  /**
   * Play rocket launch sound
   */
  playRocketLaunch(): void {
    if (!this.audioContext) return;

    const duration = 0.5;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    const filter = this.audioContext.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, this.audioContext.currentTime + duration);

    filter.type = 'lowpass';
    filter.frequency.value = 400;

    gain.gain.setValueAtTime(0.2, this.audioContext.currentTime);
    gain.gain.linearRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    osc.start();
    osc.stop(this.audioContext.currentTime + duration);

    // Add whoosh noise
    this.playWhoosh(duration);
  }

  /**
   * Play Hellfire launch sound
   */
  playHellfireLaunch(): void {
    if (!this.audioContext) return;

    const duration = 0.8;
    
    // Lower frequency for larger missile
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    const filter = this.audioContext.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(150, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(70, this.audioContext.currentTime + duration);

    filter.type = 'lowpass';
    filter.frequency.value = 300;

    gain.gain.setValueAtTime(0.25, this.audioContext.currentTime);
    gain.gain.linearRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    osc.start();
    osc.stop(this.audioContext.currentTime + duration);

    // Voice warning
    this.playVoiceWarning('MISSILE_LAUNCH');
    
    this.playWhoosh(duration);
  }

  /**
   * Play whoosh sound (white noise)
   */
  private playWhoosh(duration: number): void {
    if (!this.audioContext) return;

    const noise = this.audioContext.createBufferSource();
    const buffer = this.audioContext.createBuffer(
      1,
      this.audioContext.sampleRate * duration,
      this.audioContext.sampleRate
    );
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    noise.buffer = buffer;

    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1000;

    const gain = this.audioContext.createGain();
    gain.gain.setValueAtTime(0.1, this.audioContext.currentTime);
    gain.gain.linearRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    noise.start();
  }

  /**
   * Play explosion sound
   */
  playExplosion(): void {
    if (!this.audioContext) return;

    const duration = 1.5;

    // Low rumble
    const osc = this.audioContext.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(80, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(30, this.audioContext.currentTime + duration);

    const oscGain = this.audioContext.createGain();
    oscGain.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    oscGain.gain.linearRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    osc.connect(oscGain);
    oscGain.connect(this.masterGain!);

    osc.start();
    osc.stop(this.audioContext.currentTime + duration);

    // Noise burst
    const noise = this.audioContext.createBufferSource();
    const buffer = this.audioContext.createBuffer(
      1,
      this.audioContext.sampleRate * duration,
      this.audioContext.sampleRate
    );
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < data.length; i++) {
      const envelope = Math.exp(-i / (this.audioContext.sampleRate * 0.3));
      data[i] = (Math.random() * 2 - 1) * envelope;
    }
    
    noise.buffer = buffer;

    const noiseGain = this.audioContext.createGain();
    noiseGain.gain.value = 0.2;

    noise.connect(noiseGain);
    noiseGain.connect(this.masterGain!);

    noise.start();
  }

  /**
   * Initialize voice warning utterances
   */
  private initializeWarnings(): void {
    const warnings = {
      MISSILE_LAUNCH: 'MISSILE LAUNCH',
    };

    Object.entries(warnings).forEach(([key, text]) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.2;
      utterance.pitch = 0.8;
      utterance.volume = 0.7;
      this.utterances.set(key, utterance);
    });
  }

  /**
   * Play voice warning (Betty)
   */
  playVoiceWarning(warningType: string): void {
    if (!window.speechSynthesis) return;

    const baseUtterance = this.utterances.get(warningType);
    if (baseUtterance) {
      // Create new utterance to avoid reuse issues
      const utterance = new SpeechSynthesisUtterance(baseUtterance.text);
      utterance.rate = baseUtterance.rate;
      utterance.pitch = baseUtterance.pitch;
      utterance.volume = baseUtterance.volume;
      
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    }
  }

  /**
   * Set master volume
   */
  setVolume(volume: number): void {
    if (this.masterGain) {
      this.masterGain.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Check if audio is initialized
   */
  isInitialized(): boolean {
    return this.audioContext !== null;
  }

  /**
   * Dispose audio resources
   */
  dispose(): void {
    this.stopRotor();
    
    if (this.rotorLFO) {
      try {
        this.rotorLFO.stop();
        this.rotorLFO.disconnect();
      } catch (e) {
        // Already stopped
      }
    }
    
    if (this.rotorNoiseNode) {
      this.rotorNoiseNode.disconnect();
    }
    
    if (this.audioContext) {
      this.audioContext.close();
    }
  }
}
